// Central category catalog + field schema
// Keep in sync with backend CATEGORY_CATALOG (server.py)

export const CATEGORY_LIST = [
  { key: "home",       label: "Homes / Flats",     icon: "Home" },
  { key: "pg",         label: "PG / Co-living",    icon: "Users" },
  { key: "room",       label: "Private Rooms",     icon: "BedDouble" },
  { key: "short_stay", label: "Short-term Stays",  icon: "CalendarDays" },
  { key: "shop",       label: "Shops",             icon: "Store" },
  { key: "factory",    label: "Factory",           icon: "Factory" },
  { key: "cafe",       label: "Cafe / Restaurant", icon: "Utensils" },
  { key: "warehouse",  label: "Warehouse",         icon: "Warehouse" },
  { key: "land",       label: "Land",              icon: "Trees" },
];

export const CATEGORY_MAP = Object.fromEntries(CATEGORY_LIST.map((c) => [c.key, c]));

// Fields per category. Each field: { key, label, type, options?, required?, help? }
export const CATEGORY_FIELDS = {
  home: [
    { key: "bhk",         label: "BHK",         type: "select", options: ["1","2","3","4","5"], required: true },
    { key: "furnishing",  label: "Furnishing",  type: "select", options: [["unfurnished","Unfurnished"],["semi","Semi-Furnished"],["fully","Fully Furnished"]], required: true },
    { key: "area_sqft",   label: "Area (sqft)", type: "number" },
    { key: "lease_months",label: "Lease (months)", type: "number" },
  ],
  pg: [
    { key: "sharing_type", label: "Sharing",     type: "select", options: [["single","Single"],["double","Double"],["triple","Triple"],["quad","4+ sharing"]], required: true },
    { key: "gender_pref",  label: "Gender pref", type: "select", options: [["male","Male"],["female","Female"],["any","Any"]], required: true },
    { key: "meals_included", label: "Meals included", type: "bool" },
    { key: "curfew",       label: "Curfew",      type: "text", help: "e.g. 11 PM, None" },
    { key: "house_rules",  label: "House rules", type: "textarea" },
  ],
  room: [
    { key: "attached_bathroom", label: "Attached bathroom", type: "bool" },
    { key: "shared_areas",      label: "Shared areas",      type: "multi", options: ["Kitchen","Living room","Terrace","Balcony","Garden"] },
    { key: "house_rules",       label: "House rules",       type: "textarea" },
  ],
  short_stay: [
    { key: "price_per_night", label: "Price / night (₹)", type: "number", required: true },
    { key: "min_nights",      label: "Min nights",         type: "number" },
    { key: "max_guests",      label: "Max guests",         type: "number" },
    { key: "check_in",        label: "Check-in time",      type: "text", help: "e.g. 2 PM" },
    { key: "check_out",       label: "Check-out time",     type: "text", help: "e.g. 11 AM" },
  ],
  shop: [
    { key: "area_sqft",     label: "Area (sqft)",     type: "number", required: true },
    { key: "frontage_type", label: "Frontage",        type: "select", options: [["Main road","Main road"],["Corner","Corner shop"],["Inside","Inside market"],["Mall","Mall unit"]] },
    { key: "frontage_ft",   label: "Frontage width (ft)", type: "number" },
    { key: "lease_months",  label: "Lease (months)",  type: "number" },
  ],
  factory: [
    { key: "area_sqft",         label: "Built-up area (sqft)", type: "number", required: true },
    { key: "power_load_kw",     label: "Power load (kW)",      type: "number", required: true },
    { key: "ceiling_height_ft", label: "Ceiling height (ft)",  type: "number" },
    { key: "lease_months",      label: "Lease (months)",       type: "number" },
  ],
  cafe: [
    { key: "seating_capacity", label: "Seating capacity",  type: "number", required: true },
    { key: "kitchen_setup",    label: "Kitchen setup ready", type: "bool" },
    { key: "exhaust_ready",    label: "Exhaust already installed", type: "bool" },
    { key: "lease_months",     label: "Lease (months)",     type: "number" },
  ],
  warehouse: [
    { key: "area_sqft",         label: "Storage area (sqft)", type: "number", required: true },
    { key: "loading_dock",      label: "Loading dock",        type: "bool" },
    { key: "ceiling_height_ft", label: "Ceiling height (ft)", type: "number" },
    { key: "lease_months",      label: "Lease (months)",      type: "number" },
  ],
  land: [
    { key: "plot_area_sqft", label: "Plot area (sqft)", type: "number", required: true },
    { key: "land_type",      label: "Land type",        type: "select", options: [["agricultural","Agricultural"],["commercial","Commercial"],["residential","Residential"],["industrial","Industrial"]], required: true },
    { key: "lease_months",   label: "Lease (months)",   type: "number" },
  ],
};

// Filter fields shown on Listings page per category (subset for filter UX)
export const CATEGORY_FILTERS = {
  home:       [{ key: "bhk", label: "BHK", type: "select", options: ["1","2","3","4"] },
               { key: "furnishing", label: "Furnishing", type: "select", options: [["unfurnished","Unfurnished"],["semi","Semi"],["fully","Fully"]] }],
  pg:         [{ key: "sharing_type", label: "Sharing", type: "select", options: [["single","Single"],["double","Double"],["triple","Triple"]] },
               { key: "gender_pref", label: "Gender", type: "select", options: [["male","Male"],["female","Female"],["any","Any"]] }],
  room:       [],
  short_stay: [{ key: "max_price_per_night", label: "Max ₹/night", type: "number", max: true, target: "price_per_night" }],
  shop:       [{ key: "min_area_sqft", label: "Min area (sqft)", type: "number", min: true, target: "area_sqft" }],
  factory:    [{ key: "min_area_sqft", label: "Min area (sqft)", type: "number", min: true, target: "area_sqft" },
               { key: "min_power_load_kw", label: "Min power (kW)", type: "number", min: true, target: "power_load_kw" }],
  cafe:       [{ key: "min_seating_capacity", label: "Min seats", type: "number", min: true, target: "seating_capacity" }],
  warehouse:  [{ key: "min_area_sqft", label: "Min area (sqft)", type: "number", min: true, target: "area_sqft" }],
  land:       [{ key: "land_type", label: "Land type", type: "select", options: [["agricultural","Agricultural"],["commercial","Commercial"],["residential","Residential"],["industrial","Industrial"]] }],
};

// Display: which category-field to show as headline "meta" on cards
export function categoryHeadline(cat, cf = {}) {
  switch (cat) {
    case "home": return `${cf.bhk || "-"} BHK · ${cf.furnishing || ""}`.replace(/ · $/, "");
    case "pg": return `${cf.sharing_type || "-"} · ${cf.gender_pref || "any"}`;
    case "room": return cf.attached_bathroom ? "Attached bathroom" : "Shared bathroom";
    case "short_stay": return `₹${cf.price_per_night || 0}/night · ${cf.max_guests || 0} guests`;
    case "shop": return `${cf.area_sqft || 0} sqft · ${cf.frontage_type || ""}`.trim();
    case "factory": return `${cf.area_sqft || 0} sqft · ${cf.power_load_kw || 0}kW`;
    case "cafe": return `${cf.seating_capacity || 0} seats${cf.kitchen_setup ? " · Kitchen ready" : ""}`;
    case "warehouse": return `${cf.area_sqft || 0} sqft${cf.loading_dock ? " · Dock" : ""}`;
    case "land": return `${cf.plot_area_sqft || 0} sqft · ${cf.land_type || ""}`;
    default: return "";
  }
}
